<template>
	<div>
		<div v-if="table">
			<div class="box">
				<div class="row align-items-end mb-2">
					<div class="col-6 col-md-3">
						<label>{{ $t('gynecologie.saison') }}</label>
						<SeasonInput v-model="seasonSelected" @change="loadSeasonFilters" :multiple="multiple_seasons"/>
					</div>
					<div class="col-6 col-md-3">
						<label>{{ $t('monte.etalon') }}</label>
						<e-select
							v-model="selectedStallion"
							track-by="id"
							label="label"
							:options="filterStallions"
							:searchable="true"
							:show-labels="false"
							:allow-empty="!stallionId"
							:sortable="false"
							:disabled="!!stallionId"
							:placeholder="$t('monte.tous_etalons')"
							@input="onFilterChange"
						>
							<template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>
					<div class="col-6 col-md-3">
						<label>{{ $t('monte.client') }}</label>
						<e-select
							v-model="selectedClient"
							track-by="id"
							label="label"
							:options="filterClients"
							:searchable="true"
							:show-labels="false"
							:allow-empty="!clientId"
							:sortable="false"
							:disabled="!!clientId"
							:placeholder="$t('monte.tous_clients')"
							@input="onFilterChange"
						>
							<template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>
					<div class="col-6 col-md-3">
						<label>{{ $t('monte.cmep') }}</label>
						<e-select
							v-model="selectedCmep"
							track-by="id"
							label="label"
							:options="filterCmeps"
							:searchable="true"
							:show-labels="false"
							:allow-empty="true"
							:sortable="false"
							:placeholder="$t('monte.tous_cmep')"
							@input="onFilterChange"
						>
							<template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>
				</div>

				<template v-if="seasonSelected != null && contract_ids != null">
					<CustomTable
						id_table="contract"
						primaryKey="contract_id"
						:externSlotColumns="extern_slot_columns"
						:hide_if_empty="true"
						:rawColumns="rawColumnNames"
						:hrefsRoutes="config_table_hrefs"
						:items="contracts"
						ref="contractTable"
						:columsAddBegin="['preview']"
						:busy.sync="table_busy"
						:onlineMode="true"
						:serverTotalRows="serverTotalRows"
						:numberColumns="contractNumberColumns"
						@onTableParamsChange="onTableParamsChange"
					>
						<template v-slot:[`custom-slot-cell(avenant.avenant_free)`]="{ data }">
							<font-awesome-icon class="text-success" v-if="data.avenant && data.avenant.avenant_free" :icon="['fal', 'check-circle']" />
							<font-awesome-icon class="text-danger" v-else :icon="['fal', 'times-circle']" />
						</template>
						<template v-slot:[`custom-slot-cell(avenant.type_monte.contract_type_monte.contracttypemonte_label)`]="{ data }">
							<span v-if="data.avenant.type_monte">{{ $t(data.avenant.type_monte.contract_type_monte.contracttypemonte_label) }}</span>
						</template>
						<template v-slot:[`custom-slot-cell(avenant.status.avenantstatus_label)`]="{ data }">
							{{ $t(data.avenant.status.avenantstatus_label) }}
						</template>
						<template v-slot:[`custom-slot-cell(avenant.avenant_dn_bool)`]="{ data }">
							<font-awesome-icon class="text-success" v-if="data.avenant && data.avenant.avenant_dn_bool" :icon="['fal', 'check-circle']" />
							<font-awesome-icon class="text-danger" v-else :icon="['fal', 'times-circle']" />
						</template>
						<template v-slot:[`custom-slot-cell(avenant.avenant_dn)`]="{ data }">
							{{ formatDate(data.avenant.avenant_dn) }}
						</template>
						<template v-slot:[`custom-slot-cell(avenant.avenant_as_bool)`]="{ data }">
							<font-awesome-icon class="text-success" v-if="data.avenant && data.avenant.avenant_as_bool" :icon="['fal', 'check-circle']" />
							<font-awesome-icon class="text-danger" v-else :icon="['fal', 'times-circle']" />
						</template>
						<template v-slot:[`custom-slot-cell(avenant.avenant_revue_bool)`]="{ data }">
							<font-awesome-icon class="text-success" v-if="data.avenant && data.avenant.avenant_revue_bool" :icon="['fal', 'check-circle']" />
							<font-awesome-icon class="text-danger" v-else :icon="['fal', 'times-circle']" />
						</template>
						<template v-slot:[`custom-slot-cell(avenant.avenant_type_contrat)`]="{ data }">
							<p>{{ $t(data.avenant.avenant_type_contrat) }}</p>
						</template>
						<template v-slot:custom-slot-cell(formatted_invoices)="{ data }">
							<p v-for="invoice in data.formatted_invoices" :key="invoice.invoice_id">
								<a href="" class="" @click.prevent="quickPreview(invoice.invoice_id)">{{invoice.invoice_num}}</a> ({{ $t('invoice.invoice_balance') }} : {{ invoice.invoice_balance }})
							</p>
						</template>
						<template v-slot:[`custom-slot-cell(paillettes_date)`]="{ data }">
							{{ data.paillettes_date }}
						</template>
						<template v-slot:[`custom-slot-cell(doses_date)`]="{ data }">
							{{ data.doses_date }}
						</template>
						<template v-slot:[`custom-slot-cell(preview)`]="{ data }">
							<a href="" class="" @click.prevent="quickContractPreview(data.contract_id)">
								<font-awesome-icon :icon="['fas', 'eye']" />
							</a>
						</template>
						<template v-slot:[`custom-slot-cell(avenant.horse.horse_wholesire)`]="{ data }">
							<e-copybox v-if="data.avenant && data.avenant.horse" :value="data.avenant.horse.horse_wholesire"></e-copybox>
						</template>
						<template v-slot:[`custom-slot-cell(contract_sent)`]="{ data }">
							<font-awesome-icon class="text-success" v-if="data.contract_sent" :icon="['fal', 'check-circle']" />
							<font-awesome-icon class="text-danger" v-else :icon="['fal', 'times-circle']" />
						</template>
						<template v-slot:[`custom-slot-cell(contract_document)`]="{ data }">
							<font-awesome-icon class="text-success" v-if="data.contract_document" :icon="['fal', 'check-circle']" />
							<font-awesome-icon class="text-danger" v-else :icon="['fal', 'times-circle']" />
						</template>
						<template v-slot:custom-slot-cell(trackings_link)="{ data }">
							<a href="" class="" @click.prevent="openShutterTrackings(data.contract_id)">{{ $t('global.see_trackings') }}</a>
						</template>
						<template v-slot:[`custom-slot-cell(tiers_whatsapp)`]="{ data }">
							<a v-if="data.tiers_phone !== ''" :href="`https://wa.me/${data.tiers_phone}`" target="_blank">
								<font-awesome-icon :icon="['fab', 'whatsapp']" />
							</a>
						</template>
					</CustomTable>
				</template>
			</div>
		</div>
		<div v-else>
			<main>
				<HeaderTab 
					:title="getTrad('monte.contract_list')" 
					:back_action="back_action"
				/>
				<div id="content">
					<ContractShutter :contract_id="contract_id" :version="version"></ContractShutter>
				</div>
			</main>
		</div>

		<b-modal size="xl" ref="modelPreview" hide-footer @hidden="base64 = null">
            <template v-slot:modal-title>
                {{ $t("action.previsualiser") }}
            </template>

            <iframe v-if="base64 != null" :src="'data:application/pdf;base64,' + base64" height="1000px" width="100%" style="position:relative"></iframe>
            <div v-else>
                <LoadingSpinner />
            </div>
        </b-modal>

		<b-modal ref="modalEnvoie" hide-footer>
			<template v-slot:modal-title>
				{{ $t("courrier_type.ajout") }}
			</template>

			<e-select
	            v-model="model_selected"
	            id="models"
	            track-by="model_label"
	            label="model_label"
	            class="primary"
	            :options="models"
	            :searchable="true"
	            :show-labels="false"
	            :allow-empty="false"
	        >
	            <template slot="singleLabel" slot-scope="{ option }">{{ option.model_label }}</template>
	            <template slot="noOptions">{{ $t('global.list_empty') }}</template>
	        </e-select>

	        <div v-if="model_selected" class="text-center">
	            <b-button pill variant="primary" class="mt-3" @click.prevent="addModelCourrier"><font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse /> {{ $t("courrier_type.choisir") }}</b-button>
	        </div>
		</b-modal>

		<ModalContractEnvoie ref="modal_contract_envoie" />
		<ModalContractModifRapidev2 ref="modal_contract_modif_rapide" @submit="refreshTable"/>
		<ModalSendContract ref="modal_send_contract" @submit="refreshTable"/>
		<ModalEditQualification ref="modal_edit_qualification"/>
		<ModalActDGLot ref="modal_act_dg_lot" @submit="refreshAfterDG"/>
		<ModalAskDPS ref="modal_ask_dps" @submit="refreshTable"/>
		<ModalExportGlobal v-if="seasonSelected" ref="modal_export" :season_id="seasonSelected.id" />
		<ModalRelanceCentre ref="modal_relance_centre"/>
	</div>
</template>

<script type="text/javascript">
	import { EventBus } from 'EventBus'
	import TableAction from "@/mixins/TableAction.js"
	import Navigation from "@/mixins/Navigation.js"
	import ContractMixin from "@/mixins/Contract.js"
	import Model from "@/mixins/Model.js"
	import Common from '@/assets/js/common'
	import TiersMixin from "@/mixins/Tiers.js"
	import AccountingMixin from "@/mixins/Accounting.js"
	import AccountingAccountMixin from "@/mixins/AccountingAccount.js"
	import ArticleMixin from "@/mixins/Article.js"
	import Syndic from "@/mixins/Syndic.js"
	import Invoice from "@/mixins/Invoice.js"
	import Gynecologie from "@/mixins/Gynecologie.js"
	import Shutter from '@/mixins/Shutter.js'
	import ShutterContract from '@/mixins/shutters-manager/Contract.js'
	import ActeType from "@/mixins/ActeType.js"
    import _groupBy from 'lodash/groupBy'
	import ModalAskDPS from '@/components/Modals/ModalAskDPS.vue'


	export default {
		name: "ContractListOnline",
		mixins: [TableAction, Navigation, ContractMixin, Model, TiersMixin, AccountingMixin, AccountingAccountMixin, ArticleMixin, Syndic, Invoice, Shutter, ShutterContract, ActeType, Gynecologie],
		props: {
			horse: { type: Object, default: null },
			stallionId: { type: Number, default: null },
			clientId: { type: Number, default: null },
			mareId: { type: Number, default: null },
			lieuMonteId: { type: Number, default: null },
			courtierId: { type: Number, default: null }
		},
		data () {
			return {
                seasonSelected: null,
				base64: null,
                filters: {},
				table_busy: false,
				table: true,
				contract_id: null,
				contract_ids: [],
				currentTableParams: null,
				events_tab: {
					'Swipper::TabClicked': (params) => this.handleTabEvent(params),
					'ContractMareList::reloadTabs': (params) => this.handleTabEvent(params),
					'TableAction::goToAddContract': async () => {
						if(this.processing) return
						this.processing = true
						this.contract_id = this.clientId ? await this.addContractOnlineFromTiers(this.clientId) : await this.addContractOnline();
						this.processing = false
						const routeParams = { contract_id: parseInt(this.contract_id) }
						if (this.stallionId) routeParams.stallion_id = this.stallionId
						if (this.clientId) routeParams.tier_id = this.clientId
						if (this.mareId) routeParams.mare_id = this.mareId
						this.$router.push({name: "ContractFormv2", params: routeParams})
						this.table = false
					},
					'TableAction::goToEditContract': (params) => {
						this.$router.push({name: "ContractFormv2", params: {contract_id: parseInt(params.contract_id)}})
						this.contract_id = parseInt(params.contract_id);
						this.table = false
					},
					'TableAction::goToDupliqueContract': async (params) => {
						this.contract_id = await this.duplicateContractOnline(params[0].contract_id);
						this.$router.push({name: "ContractFormv2", params: {contract_id: parseInt(this.contract_id)}})
						this.table = false
					},
					'TableAction::goToPrintContract': (contract_ids) => {
						this.callPrintContracts(contract_ids)
					},
					'TableAction::goToSendContract': (contract_ids) => {
						this.openModalSendContract(contract_ids)
					},
					'TableAction::goToCourrierContract': (contract_ids) => {
						this.callCourrierContracts(contract_ids)
					},
					'TableAction::goToDeleteContract': (contract_ids) => {
						this.deleteContract(contract_ids)
					},
					'TableAction::goToFastEditContract': (contract) => {
						this.openModalFastEditContract(contract[0])
					},
					'TableAction::goToMarkContractAsSended': (contract_ids) => {
						this.markAsSended(contract_ids)
					},
					'TableAction::goToExportContract': () => {
						this.openExportModal()
					},
					'TableAction::goToEditQualificationTiers': (contracts) => {
						this.openQualificationModal(contracts)
					},
					'TableAction::goToActDgLotContract': (contracts) => {
						this.openModalActDGLot(contracts)
					},
					'TableAction::goToAskDPS': (contracts) => {
						this.askDPS(contracts)
					},
					'TableAction::goToPrintRelanceContract': (contracts_ids) => {
						this.$refs.modal_relance_centre.openModal(this.clientId, contracts_ids)
					}
				},
				rawColumnNames: [
					"articles",
					"seasonmarestallion.seasonmarestallion_status",
					"contract_note",
					"avenant.avenant_justification",
					"avenant.avenant_comment"
				],
				config_table_hrefs: {
					'contract_num': {
						routeUniqueName: 'ContractFormv2',
						routeName: 'ContractFormv2',
						params: {
							contract_id: 'contract_id'
						}
					},
					'contract_tiers[0].tiers.tiers_rs': {
						routeUniqueName: 'tiersTableauBord',
						routeName: 'tiersTableauBord',
						params: {
							tiers_id: 'contract_tiers.0.tiers.tiers_id'
						}
					},
					'avenant.horse.horse_nom': {
						routeUniqueName: 'MareFiche',
						routeName: 'horseFiche',
						params: {
							horse_id: 'avenant.horse.horse_id'
						}
					},
					'config.horse.horse_nom': {
						routeUniqueName: 'StallionFiche',
						routeName: 'horseFiche',
						params: {
							horse_id: 'config.horse.horse_id'
						}
					},
					'avenant.cmep.tiers_rs': {
						routeUniqueName: 'cmepFiche',
                        routeName: 'tiersFiche',
                        params: {
                            tiers_id: 'avenant.cmep.tiers_id'
                        }
                    },
					'courtier.tiers_rs': {
						routeUniqueName: 'courtierFiche',
                        routeName: 'tiersFiche',
                        params: {
                            tiers_id: 'courtier.tiers_id'
                        }
                    },
					'avenant.porteuse.horse_nom': {
						routeUniqueName: 'MarePorteuseFiche',
						routeName: 'horseFiche',
						params: {
							horse_id: 'avenant.porteuse.horse_id'
						}
					}
				},
				extern_slot_columns: [
					'avenant.avenant_free',
					'avenant.avenant_dn_bool',
					'avenant.avenant_dn',
					'avenant.avenant_as_bool',
					'avenant.avenant_revue_bool',
					'avenant.avenant_type_contrat',
					'avenant.type_monte.contract_type_monte.contracttypemonte_label',
					'formatted_invoices',
					'paillettes_date',
					'doses_date',
					'contract_document',
					'preview',
					'trackings_link',
					'avenant.horse.horse_wholesire',
					'tiers_whatsapp',
					'avenant.status.avenantstatus_label'
				],

				model_selected: {},
				models: [],
				contracts_selected: [],
				processing: false,
				version: 2,
				customGroupBy: {'avenant.type_monte.contract_type_monte.contracttypemonte_label': this.getTrad},
				actes_types: null,
				contract_form: {},
				contracts: [],
				serverTotalRows: -1,
				selectedStallion: null,
				selectedClient: null,
				selectedCmep: null,
				filterStallions: [],
				filterClients: [],
				filterCmeps: []
			}
		},
		created() {
			this.init_component()
		},
		methods: {
			/**
			 * Gère l'événement de clic/reload sur l'onglet ContractMareList.
			 * @param {Object} params - Paramètres de l'événement (contient href)
			 */
			handleTabEvent(params) {
				if(params.href == "ContractMareList" && !this.table_busy) {
					this.table = true
				}
			},
			async init_component() {
				this.table_busy = true

				// Présélectionner l'étalon immédiatement si stallionId est fourni
				if (this.stallionId) {
					this.selectedStallion = { id: this.stallionId, label: '' }
				}

				// Présélectionner le client immédiatement si clientId est fourni
				if (this.clientId) {
					this.selectedClient = { id: this.clientId, label: '' }
				}

				// Présélectionner le cmep immédiatement si lieuMonteId est fourni
				if (this.lieuMonteId) {
					this.selectedCmep = { id: this.lieuMonteId, label: ''}
				}

				if(this.$route.params.contract_id)
				{
					this.table = false
					this.contract_id = parseInt(this.$route.params.contract_id)
				}
				else
				{
					this.table = true
					this.contract_id = null
					this.loadSeasonFilters()
					// Ne pas appeler loadContracts() ici :
					// la CustomTable émet onTableParamsChange après sa montée,
					// ce qui déclenche loadContracts avec les bons params.
				}
			},
			/**
			 * Charge les contrats de la saison sélectionnée avec pagination serveur.
			 * @param {Object|null} tableParams - Paramètres de la CustomTable (page, tri, filtres)
			 */
			async loadContracts(tableParams = null) {
				if(this.seasonSelected) {
					this.table_busy = true
					const base = tableParams || this.currentTableParams || null
					// Copier pour ne pas muter currentTableParams
					const params = base ? JSON.parse(JSON.stringify(base)) : null

					// Fusionner les filtres sélecteurs avec les filtres de la vue
					if (params) {
						params.filters = this.buildFilters(params.filters || [])
					}

					const seasons_ids = this.multiple_seasons && Array.isArray(this.seasonSelected) ? this.seasonSelected.map(s => s.id) : [this.seasonSelected.id]
					const result = await this.getSeasonContract(seasons_ids, params)

					// Réponse paginée (POST) vs tableau brut (GET)
					if (result && result.data && result.total !== undefined) {
						this.contracts = result.data.map(contract => ({...contract, courtier: contract.courtier ? contract.courtier.tiers : null}))
						this.serverTotalRows = result.total
					} else {
						this.contracts = result || []
						this.serverTotalRows = -1
					}

					this.table_busy = false
				}
			},
			/**
			 * Fusionne les filtres sélecteurs (étalon, client, CMEP) avec les filtres de la CustomTable.
			 * @param {Array} viewFilters - Filtres provenant de la CustomTable
			 * @returns {Array} Filtres combinés
			 */
			buildFilters(viewFilters) {
				const filters = [...viewFilters]
				if (this.selectedStallion) {
					filters.push({ column: 'config.horse.horse_id', operator: 'isEqualTo', value: this.selectedStallion.id })
				}
				if (this.selectedClient) {
					filters.push({ column: 'contract_tiers[0].tiers.tiers_id', operator: 'isEqualTo', value: this.selectedClient.id })
				}
				if (this.selectedCmep) {
					filters.push({ column: 'avenant.cmep.tiers_id', operator: 'isEqualTo', value: this.selectedCmep.id })
				}
				if (this.mareId) {
					filters.push({ column: 'avenant.horse.horse_id', operator: 'isEqualTo', value: this.mareId })
				}
				if (this.courtierId) {
					filters.push({ column: 'courtier.tiers.tiers_id', operator: 'isEqualTo', value: this.courtierId })
				}
				return filters
			},
			onFilterChange() {
				if (this.currentTableParams) {
					this.loadContracts()
				}
			},
			/**
			 * Charge les filtres de sélection (étalons, clients, CMEP) pour la saison en cours.
			 */
			async loadSeasonFilters() {
				if (!this.seasonSelected) return
				const seasons_ids = this.multiple_seasons && Array.isArray(this.seasonSelected) ? this.seasonSelected.map(s => s.id) : [this.seasonSelected.id]
				const filters = await this.getSeasonFilters(seasons_ids)
				const sortByLabel = (a, b) => (a.label || '').localeCompare(b.label || '', 'fr', { sensitivity: 'base' })
				if (filters) {
					this.filterStallions = (filters.stallions || []).sort(sortByLabel)
					this.filterClients = (filters.clients || []).sort(sortByLabel)
					this.filterCmeps = (filters.cmeps || []).sort(sortByLabel)
				}
				// Mettre à jour le label de l'étalon présélectionné
				if (this.stallionId && this.selectedStallion) {
					const matchStallion = this.filterStallions.find(s => s.id === this.stallionId)
					if (matchStallion) {
						this.selectedStallion = matchStallion
					}
				}
				// Mettre à jour le label du clien<t> présélectionné
				if (this.clientId && this.selectedClient) {
					const matchClient = this.filterClients.find(c => c.id === this.clientId)
					if (matchClient) {
						this.selectedClient = matchClient
					}
				}
				// Mettre à jour le label du cmep présélectionné
				if (this.lieuMonteId && this.selectedCmep) {
					const matchCmep = this.filterCmeps.find(c => c.id === this.lieuMonteId)
					if (matchCmep) {
						this.selectedCmep = matchCmep
					}
				}
			},
			/**
			 * Callback déclenché par CustomTable lors de changement de page, tri ou filtre.
			 * Mémorise les paramètres et relance le chargement.
			 * @param {Object} tableParams - { per_page, current_page, sort_by, sort_desc, filters, columns }
			 */
			onTableParamsChange(tableParams) {
				this.currentTableParams = tableParams
				this.loadContracts(tableParams)
			},
			async deleteContract(contract_ids) {
				try {
					await this.deleteContracts(contract_ids)
				}
				catch(error) {
					EventBus.$emit('App::failureToast', 'error.DC')
				}
				
				this.refreshTable()
			},
			async callSendContracts(contract_ids) {
				let result = await this.sendMailContracts(contract_ids)
				if(result != null){
					this.successToast('monte.send_success')
					await this.markSendedContract(contract_ids)
					.then(() => {
						this.refreshTable()
					})
				}
				else
				{
					this.failureToast('monte.send_error')
				}
				EventBus.$emit("TableAction::stopSpin")
			},
			async callPrintContracts(contract_ids) {
				await Common.asyncForEach(contract_ids, async (contract_id) => {
					let result = await this.printContract(parseInt(contract_id))
					if(result == null){
						this.failureToast('monte.print_error')					
					}
					EventBus.$emit("TableAction::stopSpin")
				})
			},
			async callCourrierContracts(contract_ids) {
				this.$refs.modal_contract_envoie.openModal(contract_ids)
			},
			async askDPS(contracts) {
				const items = contracts.map(contract => {
					return {
						mare_id: contract.avenant.horse ? contract.avenant.horse.horse_id : null,
						season_id: contract.config.season.season_id
					}
				})
				// Plusieurs saisons peuvent être sélectionnées : chaque contrat porte sa propre saison
                this.$refs['modal_ask_dps'].openModal(null, items)
            },
			unselectAll() {
        		this.$refs.contractTable.unselectAll()
        	},
			back_action(){
				this.goBack()
				this.table = true
				if (this.$refs.contractTable) {
					this.$refs.contractTable.refreshTable()
				}
			},
			openModalFastEditContract(contract) {
				let contract_avenant = contract.avenant
				let infos_contract = {
					contract_id: contract.contract_id,
					contract_note: contract.contract_note,
					contractconfig_horse: contract.config.horse.horse_id,
					contractconfig_season: contract.config.season.season_id
				}
				let infos_avenant = {
					avenant_id: contract_avenant.avenant_id, 
					avenant_dn: contract_avenant.avenant_dn,
					avenant_dn_bool: contract_avenant.avenant_dn_bool,
					avenant_dps: contract_avenant.avenant_dps, 
					avenant_as_bool: contract_avenant.avenant_as_bool, 
					avenant_date_signature: contract_avenant.avenant_date_signature, 
					avenant_date_validation: contract_avenant.avenant_date_validation, 
					avenant_status: contract_avenant.avenant_status,
					avenant_type_contrat: contract_avenant.avenant_type_contrat,
					avenant_comment: contract_avenant.avenant_comment
				}
				this.$refs.modal_contract_modif_rapide.openModal(infos_contract, infos_avenant)
			},
			async markAsSended(contract_ids) {
				await this.markSendedContract(contract_ids)
				.then(() => {
					this.refreshTable()
				})
			},
			openModalSendContract(contract_ids) {
				this.$refs.modal_send_contract.openModal(contract_ids)
			},
			refreshTable() {
				EventBus.$emit("TableAction::stopSpin")
				this.loadContracts()
				this.$refs.contractTable.unselectAll()
			},
			async refreshAfterDG() {
				await this.$sync.force(true, true)
				this.refreshTable()
			},
			openQualificationModal(contract_ids) {
				this.$refs.modal_edit_qualification.openModal(contract_ids)
			},
			async quickContractPreview(contract_id) {
                this.$refs.modelPreview.show()
                let retour = await this.printContract(contract_id, true)
                if(retour) {
                    this.base64 = retour
                }
            },
			async quickPreview(invoice_id) {
                this.$refs.modelPreview.show()
                let retour = await this.pdfInvoice([invoice_id], "", true)
                if(retour) {
                    this.base64 = retour
                }
            },
			openExportModal() {
				this.$refs.modal_export.openModal()
			},
			async openModalActDGLot(contracts) {
				const contract_ids = contracts.map(c => c.contract_id)
				const fullContracts = await this.getContractsOnlineBatch(contract_ids)

				if(!fullContracts) {
					this.failureToast("toast.dg_lot_error")
					return
				}

				const couples = []
				let error = false

				fullContracts.forEach((data) => {
					const contract = data ? data.contract : null
					if(!contract || !contract.last_avenant || !contract.contract_config || !contract.seasonmarestallion || !contract.seasonmarestallion.seasonmarestallion_id) {
						error = true
						return
					}

					const porteuse_id = contract.last_avenant.porteuse ? contract.last_avenant.porteuse.horse_id : null
					const mare_id = contract.last_avenant.horse ? contract.last_avenant.horse.horse_id : null

					if(!porteuse_id && !mare_id) {
						error = true
						return
					}

					const couple = {
						seasonmarestallion_id: contract.seasonmarestallion.seasonmarestallion_id,
						horse: { horse_nom: contract.contract_config.horse.horse_nom }
					}

					if(porteuse_id) {
						couple.porteuse = { horse: { horse_id: porteuse_id } }
					} else {
						couple.mare = { horse: { horse_id: mare_id } }
					}

					couples.push(couple)
				})

				if(error) {
					this.failureToast("toast.dg_lot_error")
				} else {
					this.$refs.modal_act_dg_lot.openModal(couples)
				}
			},
			openShutterTrackings(contract_id) {
                this.openTrackingsShutter(contract_id)
            }
		},
		watch: {
			$route(){
				if(this.$route.params.contract_id)
				{
					this.table = false
					this.contract_id = parseInt(this.$route.params.contract_id)
				}
				else
				{
					this.table = true
					this.contract_id = null
				}
			},
			seasonSelected() {
				if (this.currentTableParams) {
					// La table est déjà montée, re-fetch avec les params courants
					// (les sélections étalon/client/lieu de monte sont conservées d'une saison à l'autre)
					this.loadContracts()
				}
			}
		},
		computed: {
			multiple_seasons() {
				return !!this.clientId || !!this.mareId || !!this.lieuMonteId || !!this.courtierId
			}
		},
		components: {
			HeaderTab: () => import('@/components/HeaderTab'),
			LoadingSpinner : () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			CustomTable : () => import('GroomyRoot/components/Table/CustomTable'),
			ContractShutter : () => import('@/components/Contract/ContractShutter'),
			SeasonInput : () => import('@/components/Inputs/SeasonInput'),
			ModalContractModifRapidev2: () => import('@/components/Contract/ModalContractModifRapidev2'),
			ModalSendContract: () => import('@/components/Contract/ModalSendContract'),
			ModalEditQualification: () => import('@/components/Contract/ModalEditQualification'),
			ModalActDGLot : () => import('@/components/Gynecologie/ModalActDGLot'),
			ModalAskDPS: () => import('@/components/Modals/ModalAskDPS'),
			ModalContractEnvoie : () => import('@/components/Contract/ModalContractEnvoie'),
			ModalExportGlobal : () => import('@/components/Contract/ModalExportGlobal'),
			ModalRelanceCentre : () => import('@/components/Modals/ModalRelanceCentre')
		},
	}
</script>
